import React from "react";

import CloseIcon from "@mui/icons-material/Close";
import { IconButton, Stack, Typography } from "@mui/material";
import { SxProps, Theme } from "@mui/material/styles";
import { ProgressOverlay } from "components";
import { DealBase } from "store/types";

import { BasicFormScreen } from "./components/BasicFormScreen";
import { CharacteristicsScreen } from "./components/CharacteristicsScreen";
import { ChoiceScreen } from "./components/ChoiceScreen";
import { SummaryScreen } from "./components/SummaryScreen";
import { DealCreateProvider, useDealCreateContext } from "./context/DealCreateContext";

import "./DealCreate.styles.scss";

interface Props {
  stageId?: number;
  dealName?: string;
  status_id?: number;
  isModal?: boolean; // NEW - defaults to true for backward compatibility
  onComplete?: (dealId: number) => void; // NEW - callback on completion (for non-modal)
  onCancel: () => void;
  onSubmit: (data: DealBase) => void;
}

interface DealCreateContentProps {
  isModal: boolean;
}

const DealCreateContent: React.FC<DealCreateContentProps> = ({ isModal }) => {
  const { state, onCancel, submitting, referenceLoading } = useDealCreateContext();

  const renderScreen = () => {
    switch (state.currentScreen) {
      case "choice":
        return <ChoiceScreen />;
      case "basic-form":
        return <BasicFormScreen />;
      case "characteristics":
        return <CharacteristicsScreen />;
      case "summary":
        return <SummaryScreen />;
      default:
        return <ChoiceScreen />;
    }
  };

  const screenWidth = {
    choice: "606px",
    "basic-form": "800px",
    characteristics: "800px",
    summary: "800px"
  };

  const containerStyles: SxProps<Theme> = isModal
    ? {
        display: "flex",
        flexDirection: "column",
        maxHeight: "88vh",
        minWidth: screenWidth[state.currentScreen],
        maxWidth: screenWidth[state.currentScreen],
        flexGrow: 1,
        position: "absolute",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        padding: 0,
        border: "none",
        overflow: "hidden",
        borderRadius: "10px",
        backgroundColor: "#fff"
      }
    : {
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        width: "100%",
        padding: 0,
        border: "none",
        overflow: "auto",
        backgroundColor: "#fff"
      };

  return (
    <ProgressOverlay inProgress={submitting || referenceLoading} containerSX={containerStyles}>
      <Stack
        direction="row"
        alignItems="center"
        sx={{
          mx: "32px",
          mt: "32px",
          pb: "8px",
          borderBottom: "1px solid #dbe8f5",
          flexShrink: 0
        }}
      >
        <Typography sx={{ flex: 1, fontSize: "18px", fontWeight: 400, lineHeight: "24px", color: "#071f35" }}>
          Create New Deal
        </Typography>
        <IconButton onClick={onCancel} sx={{ p: "4px", borderRadius: "32px" }}>
          <CloseIcon sx={{ fontSize: 16, color: "#071f35" }} />
        </IconButton>
      </Stack>
      <div className="deal-create-v2__body">{renderScreen()}</div>
    </ProgressOverlay>
  );
};

export const DealCreate: React.FC<Props> = ({
  onCancel,
  onSubmit,
  onComplete,
  stageId,
  dealName,
  status_id,
  isModal = true
}) => {
  const handleSubmit = (data: DealBase) => {
    onSubmit(data);
    if (onComplete && data.id) {
      onComplete(data.id);
    }
  };

  return (
    <DealCreateProvider
      stageId={stageId}
      dealName={dealName}
      status_id={status_id}
      onCancel={onCancel}
      onSubmit={handleSubmit}
    >
      <DealCreateContent isModal={isModal} />
    </DealCreateProvider>
  );
};
